import './App.css';
import './tvNavigation.css';
import { init } from '@noriginmedia/norigin-spatial-navigation';
import Sidebar from './components/Sidebar';
import BottomNavBar from './components/BottomNavBar';
import { Photos } from './pages/Photos';
import VideoCall from './pages/VideoCall';
import Contacts from './pages/Contacts';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import IncomingCall from './pages/IncomingCall';
import LandingPage from './pages/LandingPage';
import Account from './pages/Account';
import ResetPassword from './pages/ResetPassword';
import Messages from './pages/Messages';
import { useState, useContext, useEffect } from 'react';
import { AuthContext, AuthProvider } from './context/AuthContext';
import { SocketProvider, SocketContext } from './context/SocketContext';
import { CallProvider, useCall } from './context/CallContext';
import { ToastProvider, useToast } from './context/ToastContext';
import styled from 'styled-components';
import ErrorBoundary from './components/ErrorBoundary';

// Initialiser la navigation spatiale
init({
  debug: false,
  visualDebug: false
});

// Débloquer l'audio au premier clic/touch (politique autoplay des navigateurs)
let audioUnlocked = false;
const unlockAudio = () => {
  if (audioUnlocked) return;

  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  audioContext.resume().then(() => {
    audioUnlocked = true;
    console.log('🔊 Audio débloqué');

    // Pré-charger la sonnerie
    const ringtone = new Audio('/sounds/ringtone.mp3');
    ringtone.volume = 0;
    ringtone.play().then(() => {
      ringtone.pause();
      ringtone.volume = 1;
    }).catch(err => console.warn('Audio pre-load failed:', err.message));
  });

  document.removeEventListener('click', unlockAudio);
  document.removeEventListener('touchstart', unlockAudio);
  document.removeEventListener('keydown', unlockAudio);
};

document.addEventListener('click', unlockAudio);
document.addEventListener('touchstart', unlockAudio);
document.addEventListener('keydown', unlockAudio);

// Fonction helper pour gérer les appels manqués
const useMissedCallsHandler = () => {
  const { socket } = useContext(SocketContext);

  useEffect(() => {
    if (socket) {
      const handleMissedCall = (data) => {
        console.log('📵 Appel manqué reçu dans App.js:', data);

        try {
          const savedMissedCalls = localStorage.getItem('missedCalls');
          const missedCalls = savedMissedCalls ? JSON.parse(savedMissedCalls) : {};

          missedCalls[data.callerEmail] = {
            timestamp: data.timestamp,
            callerName: data.callerName
          };

          localStorage.setItem('missedCalls', JSON.stringify(missedCalls));
          console.log('✅ Appel manqué sauvegardé dans localStorage');
        } catch (e) {
          console.warn('localStorage error (missed calls):', e.message);
        }

        window.dispatchEvent(new CustomEvent('missedCallUpdated'));
      };

      socket.on('missed-call', handleMissedCall);
      console.log('👂 Écoute des appels manqués activée dans App.js');

      return () => {
        socket.off('missed-call', handleMissedCall);
      };
    }
  }, [socket]);
};

// Composant pour gérer les toasts des événements socket
const SocketToastHandler = () => {
  const { socket } = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const toast = useToast();

  useEffect(() => {
    if (socket) {
      const handleUserUnavailable = (data) => {
        toast.warning(`${data.targetEmail} n'est pas connecté actuellement`, {
          title: 'Contact hors ligne'
        });
      };

      const handleCallBlocked = (data) => {
        toast.error(data.reason || 'Appel non autorisé', {
          title: 'Appel bloqué'
        });
      };

      const handleNewMessage = (message) => {
        // Ne notifier que si le message est pour nous (pas envoyé par nous)
        if (user && message.sender_email !== user.email) {
          toast.info(`${message.sender_prenom}: ${message.content.substring(0, 50)}${message.content.length > 50 ? '...' : ''}`, {
            title: 'Nouveau message'
          });
        }
      };

      socket.on('user-unavailable', handleUserUnavailable);
      socket.on('call-blocked', handleCallBlocked);
      socket.on('new-message', handleNewMessage);

      return () => {
        socket.off('user-unavailable', handleUserUnavailable);
        socket.off('call-blocked', handleCallBlocked);
        socket.off('new-message', handleNewMessage);
      };
    }
  }, [socket, toast, user]);

  return null;
};

const NotificationBanner = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  color: #fff;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.5);
  z-index: 2500;
  animation: slideIn 0.3s ease;
  cursor: pointer;
  max-width: 350px;

  @keyframes slideIn {
    from {
      transform: translateX(400px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
`;

const NotificationMessage = styled.p`
  margin: 0;
  font-size: 0.9rem;
  opacity: 0.9;
`;

const HelpText = styled.div`
  position: fixed;
  bottom: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  z-index: 1500;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '💡';
  }
`;

const MainContent = styled.div`
  margin-left: ${props => props.$hasMargin ? '150px' : '0'};
  flex-grow: 1;
  transition: margin-left 350ms;

  @media (max-width: 768px) {
    margin-left: 0 !important;
    padding-bottom: ${props => props.$hasBottomNav ? 'calc(58px + env(safe-area-inset-bottom))' : '0'};
  }
`;

const HamburgerButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  border: none;
  cursor: pointer;
  z-index: 999;
  display: none;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(99, 44, 228, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }

  span {
    display: block;
    width: 24px;
    height: 3px;
    background: #fff;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <SocketProvider>
            <CallProvider>
              <AppContent />
            </CallProvider>
          </SocketProvider>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

const AppContent = () => {
  const [sidebar, setSidebar] = useState(false);
  const [currentPage, setCurrentPage] = useState('landing'); // Commence sur la landing page
  const [showHelp, setShowHelp] = useState(true);
  const [resetToken, setResetToken] = useState(null);
  const { user } = useContext(AuthContext);
  const { isInCall, callData, startCall, endCall } = useCall();
  const {
    socket,
    incomingCall,
    clearIncomingCall,
    newMediaNotification,
    clearMediaNotification,
    mediaViewedNotification,
    clearViewedNotification
  } = useContext(SocketContext);
  const toast = useToast();

  const toggleSidebar = () => setSidebar(!sidebar);
  const openSidebar = () => setSidebar(true);

  useMissedCallsHandler();

  // Detection du parametre ?reset=TOKEN dans l'URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset');
    if (token) {
      setResetToken(token);
      setCurrentPage('reset-password');
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Raccourci clavier global pour toggle la sidebar (seulement si connecté)
  useEffect(() => {
    const handleGlobalKeyPress = (e) => {
      if (e.key === 'ContextMenu' && user) {
        e.preventDefault();
        e.stopPropagation();
        toggleSidebar();
        console.log('🎮 Toggle sidebar via raccourci clavier');
      }
    };

    window.addEventListener('keydown', handleGlobalKeyPress);
    
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyPress);
    };
  }, [sidebar, user]);

  // Masquer l'aide après 10 secondes
  useEffect(() => {
    if (user) {
      const timer = setTimeout(() => {
        setShowHelp(false);
      }, 10000);
      
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    if (socket) {
      const handleCallCancelled = (data) => {
        console.log('❌ Appel annulé par l\'appelant');
        clearIncomingCall();
        openSidebar(); // Réafficher la sidebar après l'écran d'appel entrant
      };

      const handleAnsweredElsewhere = () => {
        console.log('📱 Appel décroché sur un autre appareil');
        clearIncomingCall();
        openSidebar(); // Réafficher la sidebar après l'écran d'appel entrant
      };

      socket.on('call-cancelled', handleCallCancelled);
      socket.on('answered-elsewhere', handleAnsweredElsewhere);

      return () => {
        socket.off('call-cancelled', handleCallCancelled);
        socket.off('answered-elsewhere', handleAnsweredElsewhere);
      };
    }
  }, [socket, clearIncomingCall]);

  // Gérer le refus d'appel par le destinataire
  useEffect(() => {
    if (socket) {
      const handleCallDeclined = () => {
        console.log('❌ Appel refusé par le destinataire');
        toast.warning('Appel refusé par le destinataire', {
          title: 'Appel refusé'
        });
        endCall();
        setCurrentPage('contacts');
      };

      socket.on('call-declined', handleCallDeclined);

      return () => {
        socket.off('call-declined', handleCallDeclined);
      };
    }
  }, [socket, endCall, toast]);

  // Quand l'utilisateur se connecte, le rediriger vers home et ouvrir la sidebar
  useEffect(() => {
    if (user && (currentPage === 'login' || currentPage === 'landing')) {
      setCurrentPage('home');
      openSidebar();
    }
  }, [user]);

  // Si l'utilisateur se déconnecte, retour à la landing page
  useEffect(() => {
    if (!user && currentPage !== 'landing' && currentPage !== 'login' && currentPage !== 'register' && currentPage !== 'reset-password') {
      setCurrentPage('landing');
      setSidebar(false);
    }
  }, [user, currentPage]);

  // Fermer la sidebar lors d'un appel entrant pour affichage plein écran
  useEffect(() => {
    if (incomingCall) {
      setSidebar(false);
    }
  }, [incomingCall]);

  // Fermer la sidebar lors d'un appel sortant pour affichage plein écran
  useEffect(() => {
    if (isInCall) {
      setSidebar(false);
    }
  }, [isInCall]);

  useEffect(() => {
    if (newMediaNotification) {
      const timer = setTimeout(() => {
        clearMediaNotification();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [newMediaNotification, clearMediaNotification]);

  useEffect(() => {
    if (mediaViewedNotification) {
      const timer = setTimeout(() => {
        clearViewedNotification();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [mediaViewedNotification, clearViewedNotification]);

  const handleAcceptCall = () => {
    startCall({
      roomId: incomingCall.roomId,
      contactEmail: incomingCall.callerEmail,
      contactName: incomingCall.callerName,
      isCaller: false,
      isIncoming: true
    });
    clearIncomingCall();
    setCurrentPage('appels');
  };

  const handleDeclineCall = () => {
    clearIncomingCall();
    openSidebar(); // Réafficher la sidebar après l'écran d'appel entrant
  };

  const handleMediaNotificationClick = () => {
    setCurrentPage('photos');
    clearMediaNotification();
  };

  const handleEndCall = () => {
    endCall();
    setCurrentPage('contacts');
    openSidebar();
  };

  const handleLoginClick = () => {
    setCurrentPage('login');
  };

  const renderPage = () => {
    // Appel entrant en priorité
    if (incomingCall) {
      return (
        <IncomingCall
          callerName={incomingCall.callerName}
          callerEmail={incomingCall.callerEmail}
          roomId={incomingCall.roomId}
          autoAnswer={incomingCall.autoAnswer}
          onAccept={handleAcceptCall}
          onDecline={handleDeclineCall}
        />
      );
    }

    // Landing page si pas connecté et sur landing
    if (!user && currentPage === 'landing') {
      return <LandingPage onLogin={handleLoginClick} />;
    }

    // Page reset mot de passe (publique)
    if (currentPage === 'reset-password' && resetToken) {
      return <ResetPassword resetToken={resetToken} setCurrentPage={setCurrentPage} />;
    }

    // Pages d'authentification
    if (!user && (currentPage === 'login' || currentPage === 'register')) {
      if (currentPage === 'login') {
        return <Login openSidebar={openSidebar} setCurrentPage={setCurrentPage} />;
      }
      return <Register setCurrentPage={setCurrentPage} />;
    }

    // Si pas connecté et tentative d'accès à une page protégée, rediriger vers landing
    if (!user) {
      return <LandingPage onLogin={handleLoginClick} />;
    }
    
    // Pages protégées (nécessitent connexion)
    switch (currentPage) {
      case 'home':
        return <Home sidebar={sidebar} />;
      case 'appels':
        return (
          <VideoCall 
            sidebar={sidebar}
            setCurrentPage={setCurrentPage}
            onEndCall={handleEndCall}
            isPiP={false}
            toggleSidebar={toggleSidebar}
          />
        );
      case 'contacts':
        return (
          <Contacts 
            sidebar={sidebar} 
            setCurrentPage={setCurrentPage} 
            setCallData={startCall}
            isInCall={isInCall}
            toggleSidebar={toggleSidebar}
          />
        );
      case 'photos':
        return (
          <Photos
            sidebar={sidebar}
            toggleSidebar={toggleSidebar}
          />
        );
      case 'account':
        return (
          <Account
            sidebar={sidebar}
            setCurrentPage={setCurrentPage}
            toggleSidebar={toggleSidebar}
          />
        );
      case 'messages':
        return (
          <Messages
            sidebar={sidebar}
          />
        );
      default:
        return <Home sidebar={sidebar} />;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      {/* Gestionnaire de toasts pour les événements socket */}
      <SocketToastHandler />

      {/* Sidebar uniquement si connecté */}
      {user && (
        <Sidebar sidebar={sidebar} toggleSidebar={toggleSidebar} setCurrentPage={setCurrentPage} />
      )}

      {/* Bouton hamburger quand la sidebar est cachée */}
      {user && !sidebar && (
        <HamburgerButton onClick={toggleSidebar} title="Ouvrir le menu">
          <span></span>
          <span></span>
          <span></span>
        </HamburgerButton>
      )}

      {newMediaNotification && (
        <NotificationBanner onClick={handleMediaNotificationClick}>
          <NotificationTitle>📸 Nouveaux médias reçus !</NotificationTitle>
          <NotificationMessage>
            {newMediaNotification.senderEmail} vous a envoyé {newMediaNotification.count} fichier(s)
          </NotificationMessage>
        </NotificationBanner>
      )}

      {mediaViewedNotification && (
        <NotificationBanner onClick={clearViewedNotification}>
          <NotificationTitle>👁️ Média consulté</NotificationTitle>
          <NotificationMessage>
            {mediaViewedNotification.recipientEmail} a consulté "{mediaViewedNotification.originalName}"
          </NotificationMessage>
        </NotificationBanner>
      )}

      {isInCall && currentPage !== 'appels' && (
        <VideoCall 
          sidebar={sidebar}
          setCurrentPage={setCurrentPage}
          onEndCall={handleEndCall}
          isPiP={true}
          toggleSidebar={toggleSidebar}
        />
      )}

      {/* Contenu principal */}
      <MainContent
        $hasMargin={user && sidebar}
        $hasBottomNav={user && !incomingCall && !isInCall}
      >
        {renderPage()}
      </MainContent>

      {/* Bottom nav mobile - masquée pendant les appels */}
      {user && !incomingCall && !isInCall && (
        <BottomNavBar currentPage={currentPage} setCurrentPage={setCurrentPage} />
      )}
    </div>
  );
};

export default App;